/**
 * Replaces {{Variable}} placeholders in a referral custom_message.
 *
 * Supported variables:
 *  {{ReferralLink}}  – the full sharable URL
 *  {{ReferralCode}}  – the code portion of the URL (?code=xxx)
 *  {{RewardValue}}   – e.g. "50 points" or "10% off"
 *  {{ExpiryDate}}    – formatted expiry date of the referral program
 */
export interface ReferralMessageVars {
  ReferralLink?: string;
  ReferralCode?: string;
  RewardValue?: string;
  ExpiryDate?: string;
}

export function interpolateReferralMessage(
  template: string,
  vars: ReferralMessageVars
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    return vars[key as keyof ReferralMessageVars] ?? match;
  });
}

/**
 * Extract the referral code from a referral link, whichever shape it is.
 *
 * `/r/<code>` is what the backend mints; `?code=<code>` is the older form, kept
 * because links already shared cannot be recalled. This feeds `{{ReferralCode}}`
 * in a merchant's custom share message, so getting it wrong leaves a literal
 * `{{ReferralCode}}` in the message a customer sends to their friend.
 *
 * A referral link is sometimes written with no scheme — `aye.link/r/abc123` is
 * what the merchant portal shows, because every client linkifies a bare host —
 * and `new URL` refuses that outright. Hence the fallback parse.
 */
export function extractReferralCode(referralLink: string): string | undefined {
  if (!referralLink) return undefined;

  try {
    const url = new URL(referralLink);
    const fromPath = /^\/r\/([^/?#]+)/.exec(url.pathname)?.[1];
    if (fromPath) return decodeURIComponent(fromPath);
    return url.searchParams.get('code') ?? undefined;
  } catch {
    const fromPath = /(?:^|\/)r\/([^/?#\s]+)/.exec(referralLink)?.[1];
    if (fromPath) return decodeURIComponent(fromPath);
    return /[?&]code=([^&#\s]+)/.exec(referralLink)?.[1];
  }
}

/** Format an ISO date string to a human-readable short date, e.g. "28 Feb 2026". */
export function formatExpiryDate(isoDate?: string | null): string | undefined {
  if (!isoDate) return undefined;
  try {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return undefined;
  }
}
